import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import {
  Search,
  Eye,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookingDetailModal } from "@/routes/dashboard/admin/-components/BookingDetailModal";
import { bookingQueries } from "@/domain/booking/booking.query";
import {
  BookingStatuses,
  PaymentStatuses,
  Periods,
} from "@hometoq/backend/enums";
import { getIpxImageUrl } from "@/lib/image";
import { tuyau } from "@/config/tuyau";

type AdminBookingsResponse = Awaited<
  ReturnType<typeof tuyau.bookings.getBookingsForAdmin>
>;
type AdminBooking = AdminBookingsResponse["data"][number];

export const Route = createFileRoute("/dashboard/admin/reservations")({
  loader: ({ context }) => {
    return context.queryClient.ensureQueryData(bookingQueries.adminBookings());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "accepted" | "refused" | "canceled"
  >("all");
  const [paymentFilter, setPaymentFilter] = useState<
    "all" | "completed" | "pending" | "refunded"
  >("all");
  const [sourceFilter, setSourceFilter] = useState<
    "all" | "direct" | "quote"
  >("all");
  const [selectedBooking, setSelectedBooking] = useState<AdminBooking | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: bookingsResponse, isLoading } = useQuery(
    bookingQueries.adminBookings(),
  );
  const bookings = bookingsResponse?.data ?? [];

  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const matchesSearch =
        searchTerm === "" ||
        (booking.menu?.name ?? "Menu sur-mesure")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        `${booking.bookingContact.firstName} ${booking.bookingContact.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        `${booking.chef.firstName} ${booking.chef.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        booking.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.id.toString().includes(searchTerm);

      const matchesStatus =
        statusFilter === "all" ||
        booking.bookingStatus.toLowerCase() === statusFilter;

      const matchesPayment =
        paymentFilter === "all" ||
        booking.paymentStatus.toLowerCase() === paymentFilter;

      const matchesSource =
        sourceFilter === "all" ||
        ((booking as any).source ?? "direct") === sourceFilter;

      return matchesSearch && matchesStatus && matchesPayment && matchesSource;
    });
  }, [bookings, searchTerm, statusFilter, paymentFilter, sourceFilter]);

  const handleViewBooking = (booking: AdminBooking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  };

  const getStatusBadge = (status: BookingStatuses) => {
    switch (status) {
      case BookingStatuses.PENDING:
        return (
          <Badge variant="secondary">
            <AlertTriangle className="h-3 w-3 mr-1" />
            En attente
          </Badge>
        );
      case BookingStatuses.ACCEPTED:
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Acceptée
          </Badge>
        );
      case BookingStatuses.REFUSED:
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Refusée
          </Badge>
        );
      case BookingStatuses.CANCELED:
        return (
          <Badge variant="outline">
            <XCircle className="h-3 w-3 mr-1" />
            Annulée
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPaymentBadge = (status: PaymentStatuses) => {
    switch (status) {
      case PaymentStatuses.COMPLETED:
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Payé
          </Badge>
        );
      case PaymentStatuses.PENDING:
        return (
          <Badge variant="secondary">
            <AlertTriangle className="h-3 w-3 mr-1" />
            En attente
          </Badge>
        );
      case PaymentStatuses.REFUNDED:
        return (
          <Badge variant="outline">
            <RefreshCw className="h-3 w-3 mr-1" />
            Remboursé
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatPeriod = (period: Periods) => {
    return period === Periods.MORNING ? "Matin" : "Soir";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-500">Chargement des réservations...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des Réservations
          </h1>
          <p className="text-gray-600">Gérez toutes les réservations HomeToQ</p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary">
            {filteredBookings.length} réservation
            {filteredBookings.length > 1 ? "s" : ""}
          </Badge>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Rechercher par ID, menu, client, chef, ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) =>
            setStatusFilter(
              value as "all" | "pending" | "accepted" | "refused" | "canceled",
            )
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Statut réservation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="accepted">Acceptées</SelectItem>
            <SelectItem value="refused">Refusées</SelectItem>
            <SelectItem value="canceled">Annulées</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={paymentFilter}
          onValueChange={(value) =>
            setPaymentFilter(
              value as "all" | "completed" | "pending" | "refunded",
            )
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Statut paiement" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les paiements</SelectItem>
            <SelectItem value="completed">Payé</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="refunded">Remboursé</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sourceFilter}
          onValueChange={(value) =>
            setSourceFilter(value as "all" | "direct" | "quote")
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes sources</SelectItem>
            <SelectItem value="direct">Direct</SelectItem>
            <SelectItem value="quote">Devis</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredBookings.map((booking) => (
          <Card key={booking.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">#{booking.id}</CardTitle>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">
                    {(Number(booking.price) / 100).toFixed(2)}€
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium truncate">
                    {booking.menu?.name ?? "Menu sur-mesure"}
                  </h3>
                  {(booking as any).isCustom && (
                    <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
                      Sur-mesure
                    </Badge>
                  )}
                  {!((booking as any).isCustom) &&
                    (booking as any).source === "quote" && (
                      <Badge
                        variant="outline"
                        className="border-dark-blue/20 text-dark-blue/70"
                      >
                        Via devis
                      </Badge>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(booking.bookingStatus)}
                  {getPaymentBadge(booking.paymentStatus)}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>
                    {formatDate(booking.date)} - {formatPeriod(booking.period)}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span>
                    {booking.guests} invité{booking.guests > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center text-xs">
                    {booking.bookingContact.firstName[0]}
                    {booking.bookingContact.lastName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {booking.bookingContact.firstName}{" "}
                      {booking.bookingContact.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {booking.city}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={
                        booking.chef.avatar
                          ? getIpxImageUrl(booking.chef.avatar, 150)
                          : `/ipx/f_webp,q_75,w_150/images/find_chef_avatar.webp`
                      }
                      alt={`${booking.chef.firstName} ${booking.chef.lastName}`}
                    />
                    <AvatarFallback className="text-xs">
                      {booking.chef.firstName[0]}
                      {booking.chef.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      Chef {booking.chef.firstName} {booking.chef.lastName}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewBooking(booking)}
                  className="w-full"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Voir les détails
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredBookings.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            Aucune réservation trouvée pour votre recherche.
          </p>
        </div>
      )}

      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
